/**
 * BroadcastsPage.tsx — Admin broadcast management page (Plan 08-05, D-08-10).
 *
 * Account-picker wrapper: the admin selects a creator account, then sees
 * the campaign list (CampaignList) and the 3-step composer (BroadcastComposer)
 * side by side. The account picker is required — both children need accountId.
 *
 * Admin-only surface (mounted under AdminGuard in main.tsx).
 * Semantic tokens only (bg-bg, text-text, text-text-muted, bg-bg-card, border-border).
 */

import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { accountsApi } from '../lib/api-client.js';
import { Select, SelectOption } from '@crm/ui';
import type { AccountDto } from '@crm/types';
import { CampaignList } from '../features/broadcasts/CampaignList.js';
import { BroadcastComposer } from '../features/broadcasts/BroadcastComposer.js';

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function accountLabel(a: AccountDto): string {
  return a.displayLabel || a.creatorName || `${a.id.slice(0, 8)}…`;
}

// ---------------------------------------------------------------------------
// BroadcastsPage
// ---------------------------------------------------------------------------

export function BroadcastsPage() {
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = React.useState<string>('');

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
    staleTime: 60_000,
  });

  // Invalidate campaign list when a new broadcast is created + signed off
  const handleCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['broadcasts', accountId] });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-text">Broadcasts</h1>
      </div>

      {/* Account picker */}
      <div className="max-w-xs">
        <Select
          label="Creator account"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          disabled={isLoading}
        >
          <SelectOption value="">
            {isLoading ? 'Loading accounts…' : '— Select creator —'}
          </SelectOption>
          {accounts.map((a) => (
            <SelectOption key={a.id} value={a.id}>
              {accountLabel(a)}
            </SelectOption>
          ))}
        </Select>
      </div>

      {/* Children — only mount once an account is selected */}
      {accountId && (
        <div className="flex flex-col gap-6">
          {/* Campaign list (existing campaigns for this account) */}
          <section
            className="bg-bg-card border border-border rounded-lg overflow-hidden"
            style={{ minHeight: '200px' }}
          >
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold text-text">Campaigns</h2>
            </div>
            <CampaignList accountId={accountId} />
          </section>

          {/* Broadcast composer (3-step wizard) */}
          <section className="bg-bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-sm font-semibold text-text">New Broadcast</h2>
            </div>
            <BroadcastComposer accountId={accountId} onCreated={handleCreated} />
          </section>
        </div>
      )}
    </div>
  );
}
